import { action, makeObservable, observable } from "mobx";
import * as React from "react";
import { ButtonTypes } from "../../../../../components/ui/Button";
import { Panel } from "../../../../../components/ui/Panel";
import I18n from "../../../../../core/localization/I18n";
import { UiActionRenderers } from "../../../../../core/uiAction/IUiAction";
import { Animations } from "../../../../../core/util/Animations";
import { BaseModel } from "../../../../../core/util/BaseModel";
import ImpactGroupsApi, {
  ImpactGroupsApi as IImpactGroupsApi
} from "../../../../../services/api/v2/impactGroups/ImpactGroups.api";
import ImpactsApi, { ImpactsApi as IImpactsApi } from "../../../../../services/api/v2/impacts/Impacts.api";
import ProjectsApi, { ProjectsApi as IProjectsApi } from "../../../../../services/api/v2/projects/Projects.api";
import { SingleFormModel } from "../../../forms/singleFormModel/SingleForm_model";
import { ImpactGroupExtendedViewModel } from "../ImpactGroupExtendedView_model";
import { getImpactListViewForm } from "./ImpactListView_data";
import { IModalContextModel } from "../../../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../../../core/modalZ/context/ModalContext";

export class ImpactListViewModel extends BaseModel {
  projectId: number;
  impactGroupId: number;
  modalService: IModalContextModel;
  impactService: IImpactsApi;
  projectService: IProjectsApi;
  impactGroupService: IImpactGroupsApi;
  @observable searchValue: string = "";
  @observable isSearchMode: boolean = false;
  @observable.ref owner: ImpactGroupExtendedViewModel;
  @observable impactGroupImpacts: FP.Entities.IImpact[] = [];
  @observable.ref impactGroup: FP.Entities.IImpactGroup;
  @observable.ref filteredImpacts: FP.Entities.IImpact[] = [];
  organisationId: number;

  constructor(owner: ImpactGroupExtendedViewModel) {
    super();
    makeObservable(this);
    this.owner = owner;
    this.organisationId = this.owner.organisationId;
    this.projectId = this.owner.projectId;
    this.impactGroupId = this.owner.impactGroupId;
    this.modalService = ModalContext;
    this.projectService = ProjectsApi;
    this.impactService = ImpactsApi;
    this.impactGroupService = ImpactGroupsApi;
    this.impactGroup = owner.impactGroup;

    this.filteredImpacts = this.impactGroup.impacts;
  }
  onMount = () => {};

  onUnmount = () => {
    this.resetSearch();
  };

  async loadImpactGroup() {
    await this.owner.loadImpactGroup();
    this.impactGroup = this.owner.impactGroup;
    this.filteredImpacts = this.impactGroup.impacts;
  }

  @action
  loadImpactGroupImpacts = async () => {
    const res = await this.owner.impactGroupProvider.getImpactGroupImpacts(
      this.organisationId,
      this.projectId,
      this.impactGroupId
    );
    if (!res) return;
    this.filteredImpacts = res.payload;
  };

  @action
  updateSearchValue = (e: React.FormEvent<HTMLInputElement>) => {
    this.searchValue = e.currentTarget.value;
    this.filterImpacts();
  };

  @action
  showSearchMode = () => {
    this.isSearchMode = true;
  };

  @action
  hideSearchMode = () => {
    this.isSearchMode = false;
  };

  @action
  resetSearch = () => {
    this.searchValue = "";
    this.filterImpacts();
    this.hideSearchMode();
  };

  @action
  filterImpacts = () => {
    if (this.searchValue) {
      const lowerSearch = this.searchValue.toLowerCase();
      this.filteredImpacts = this.impactGroup.impacts.filter(impact => {
        const lowerName = impact.name.toLowerCase();
        const lowerRefNo = impact.refNumber.toLowerCase();

        return lowerName.includes(lowerSearch) || lowerRefNo.includes(lowerSearch);
      });
    } else {
      this.filteredImpacts = this.impactGroup.impacts;
    }
  };

  showAddImpactModal = () => {
    const formModel = new SingleFormModel();
    formModel.formFields = getImpactListViewForm(this.organisationId, this.impactGroup);
    formModel.actions = [
      {
        id: "action1",
        label: I18n.t("phrases.cancel"),
        onAction: ev => {
          this.modalService.hide();
        },
        componentProps: {
          type: ButtonTypes.LINK,
          className: "ml-auto"
        },
        rendersIn: UiActionRenderers.BUTTON
      },
      {
        id: "action2",
        label: I18n.t("phrases.save"),
        onAction: async ev => {
          let res = await formModel.submit();
          if (!res) return;
          await this.impactGroupService.addImpactsAsync(
            this.organisationId,
            this.impactGroup.project.id,
            this.owner.impactGroup.id,
            res.impacts
          );

          this.modalService.hide();
          this.loadImpactGroup();
        },
        componentProps: {
          className: "ml-2"
        },
        rendersIn: UiActionRenderers.BUTTON
      }
    ];

    return new Promise(resolve => {
      this.modalService.show({
        showClose: true,
        title: <h1 className="mt-6">{I18n.t("phrases.addDetailedImpacts")}</h1>,
        content: <div className="container-fluid">{formModel.renderComponent()}</div>,
        componentProps: {
          wrapHeight: "full",
          wrapWidth: "small",
          position: "right",
          panelProps: {
            background: Panel.PanelBackgrounds.BG_WHITE
          }
        },
        animationOptions: {
          animateIn: Animations.SLIDE_IN_RIGHT,
          animateOut: Animations.SLIDE_OUT_RIGHT,
          speed: 5
        },
        actions: []
      });
    });
  };
}
